export type MapChunkBounds = [[number, number], [number, number]];

export interface WorldMapBounds {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
}

export interface MapPoint {
    x: number;
    y: number;
    inside: boolean;
}

export const MAP_WORLD_ORIGIN = 5200;
export const MAP_CHUNK_CENTER = 32;
export const MAP_CHUNK_SIZE = 160;
export const MAP_IMAGE_BORDER = 64;
export const MAP_IMAGE_CHUNK_SIZE = 64;

export function worldToChunk(worldX: number, worldY: number): { x: number; y: number } {
    // Chunk coordinates identify terrain centers; each chunk extends 80 units
    // on either side of that center.
    const firstChunkEdge = MAP_WORLD_ORIGIN - MAP_CHUNK_SIZE / 2;
    return {
        x: MAP_CHUNK_CENTER + Math.floor((worldX - firstChunkEdge) / MAP_CHUNK_SIZE),
        y: MAP_CHUNK_CENTER - Math.floor((worldY - firstChunkEdge) / MAP_CHUNK_SIZE),
    };
}

export function mapPathToBoundsKey(mapPath: string): string | null {
    const normalizedPath = mapPath.replace(/\\/g, "/");
    const match = normalizedPath.match(/maps\/(.+?)\/([^/]+)(?:\/|$)/i);
    return match ? `${match[1].toUpperCase()}/${match[2].toUpperCase()}` : null;
}

export function minimapImagePath(minimapPath: string): string {
    // LIST_ZONE uses mixed-case Windows paths; the served asset tree uses
    // uppercase directory and file names (important on case-sensitive hosts).
    // Converted per-zone minimaps under 3DDATA/MAPS use .jpeg; older shared
    // CONTROL/RES map images use .jpg.
    const normalizedPath = minimapPath.replace(/\\/g, "/");
    const extension = /\/MAPS\//i.test(normalizedPath) ? ".jpeg" : ".jpg";
    const path = normalizedPath.replace(/\.dds$/i, extension);
    return `/data/${path
        .split("/")
        .map(part => part.toUpperCase())
        .join("/")
        .replace(/\.(JPEG|JPG)$/, extension)}`;
}

export function chunkBoundsToWorld(bounds: MapChunkBounds): WorldMapBounds {
    const [[minChunkX, maxChunkX], [minChunkY, maxChunkY]] = bounds;

    return {
        minX: MAP_WORLD_ORIGIN + (minChunkX - MAP_CHUNK_CENTER - 0.5) * MAP_CHUNK_SIZE,
        maxX: MAP_WORLD_ORIGIN + (maxChunkX - MAP_CHUNK_CENTER + 0.5) * MAP_CHUNK_SIZE,
        minY: MAP_WORLD_ORIGIN + (MAP_CHUNK_CENTER - maxChunkY - 0.5) * MAP_CHUNK_SIZE,
        maxY: MAP_WORLD_ORIGIN + (MAP_CHUNK_CENTER - minChunkY + 0.5) * MAP_CHUNK_SIZE,
    };
}

export function minimapBoundsToWorld(
    startChunkX: number,
    startChunkY: number,
    imageWidth: number,
    imageHeight: number,
    border: number = MAP_IMAGE_BORDER
): WorldMapBounds {
    // LIST_ZONE's minimap start identifies the top-left chunk in the image.
    // Each 64-pixel image tile represents one 160-unit terrain chunk. Some
    // zones have more terrain chunks than the image actually depicts.
    const minX = MAP_WORLD_ORIGIN + (startChunkX - MAP_CHUNK_CENTER - 0.5) * MAP_CHUNK_SIZE;
    const maxY = MAP_WORLD_ORIGIN + (MAP_CHUNK_CENTER - startChunkY + 0.5) * MAP_CHUNK_SIZE;
    return {
        minX,
        maxX: minX + (imageWidth - border * 2) / MAP_IMAGE_CHUNK_SIZE * MAP_CHUNK_SIZE,
        minY: maxY - (imageHeight - border * 2) / MAP_IMAGE_CHUNK_SIZE * MAP_CHUNK_SIZE,
        maxY,
    };
}

export function worldToMapNormalized(
    worldX: number,
    worldY: number,
    bounds: WorldMapBounds
): MapPoint {
    const x = (worldX - bounds.minX) / (bounds.maxX - bounds.minX);
    const y = (bounds.maxY - worldY) / (bounds.maxY - bounds.minY);

    return {
        x,
        y,
        inside: x >= 0 && x <= 1 && y >= 0 && y <= 1,
    };
}

export function worldToMapImage(
    worldX: number,
    worldY: number,
    bounds: WorldMapBounds,
    imageWidth: number,
    imageHeight: number,
    border: number = MAP_IMAGE_BORDER
): MapPoint {
    const normalized = worldToMapNormalized(worldX, worldY, bounds);
    return {
        x: border + normalized.x * (imageWidth - border * 2),
        y: border + normalized.y * (imageHeight - border * 2),
        inside: normalized.inside,
    };
}
